import RestClient from '../../../../api/axiosInterceptor';
import { treatmentRecordsActions } from '../../../actions/user/patient/treatment_records';

/**
 * Creates an async action creator with standardized error handling and request flow
 *
 * @param {Object} actionType - Object with REQUEST, SUCCESS, and FAILURE properties
 * @param {Function} apiCall - Async function that performs the API request
 * @returns {Function} Thunk action creator function
 */
const createAsyncAction =
	(actionType, apiCall) => async (dispatch, getState) => {
	  try {
	    // Dispatch request action
	    dispatch({ type: actionType.REQUEST });

	    // Execute API call
	    const response = await apiCall();

	    // Dispatch success action with data
	    dispatch({
	      type: actionType.SUCCESS,
	      payload: response.data
	    });

	    return response;
	  } catch (error) {
	    // Extract error message with fallbacks
	    const errorMessage =
				error.response?.data?.error ||
				error.response?.data?.detail ||
				error.message ||
				'An unexpected error occurred';

	    // Dispatch failure action with error message
	    dispatch({
	      type: actionType.FAILURE,
	      payload: errorMessage
	    });

	    // Re-throw to allow component-level error handling
	    throw error;
	  }
	};

/**
 * Fetches all treatment records for the authenticated patient
 * @returns {Function} Thunk action creator
 */
export const fetchTreatmentRecords = () =>
  createAsyncAction(treatmentRecordsActions.FETCH_TREATMENT_RECORDS, () =>
    RestClient.get('/patient/treatment_records/')
  );

/**
 * Fetches a single treatment record by ID
 * @param {string} recordId - ID of the record to fetch
 * @returns {Function} Thunk action creator
 */
export const fetchSingleRecord = (recordId) =>
  createAsyncAction(treatmentRecordsActions.FETCH_SINGLE_RECORD, () =>
    RestClient.get(`/patient/treatment_records/${recordId}`)
  );

/**
 * Creates a new treatment record
 * @param {object} recordData - Data for the new record
 * @returns {Function} Thunk action creator
 */
export const createTreatmentRecord = (recordData) =>
  createAsyncAction(treatmentRecordsActions.CREATE_TREATMENT_RECORD, () =>
    RestClient.post('/patient/treatment_records/', recordData)
  );

/**
 * Updates an existing treatment record
 * @param {string} recordId - ID of the record to update
 * @param {object} recordData - Updated data for the record
 * @returns {Function} Thunk action creator
 */
export const updateTreatmentRecord = (recordId, recordData) =>
  createAsyncAction(treatmentRecordsActions.UPDATE_TREATMENT_RECORD, () =>
    RestClient.put(`/patient/treatment_records/${recordId}`, recordData)
  );

/**
 * Deletes a treatment record
 * @param {string} recordId - ID of the record to delete
 * @returns {Function} Thunk action creator
 */
export const deleteTreatmentRecord =
	(recordId) => async (dispatch, getState) => {
	  try {
	    dispatch({
	      type: treatmentRecordsActions.DELETE_TREATMENT_RECORD.REQUEST
	    });

	    const response = await RestClient.delete(
				`/patient/treatment_records/${recordId}`
	    );

	    // Special handling for delete - need to return the ID in payload
	    dispatch({
	      type: treatmentRecordsActions.DELETE_TREATMENT_RECORD.SUCCESS,
	      payload: { id: recordId }
	    });

	    return response;
	  } catch (error) {
	    const errorMessage =
				error.response?.data?.error ||
				error.response?.data?.detail ||
				'Failed to delete treatment record';

	    dispatch({
	      type: treatmentRecordsActions.DELETE_TREATMENT_RECORD.FAILURE,
	      payload: errorMessage
	    });

	    throw error;
	  }
	};

// Re-export the action creators for selected record management from actions
export {
  setSelectedRecord,
  clearSelectedRecord
} from '../../../actions/user/patient/treatment_records';
